  import React, { useState, useEffect, useRef } from 'react';
  import { createPortal } from 'react-dom';
  import { X, ChevronDown } from 'lucide-react';

  const ReusableDropdown = ({
    items = [],
    value = '',
    onChange,
    onSelect,
    placeholder = 'Type to search...',
    emptyMessage = 'No results found',
    maxItems = 8,
    getItemKey = (item) => item.id || item.email,
    getItemLabel = (item) => item.name || item.email,
    filterFunction,
    renderItem,
    theme = 'light',
    language = 'en',
    disabled = false,
    inputStyle = {},
    dropdownStyle = {},
    itemStyle = {},
    showAllOnArrowClick = true, // خاصية جديدة
    showClear = true, // control rendering of the clear (X) button
    onClear, // optional callback when clear X is clicked
  }) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const [filteredItems, setFilteredItems] = useState([]);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [showingAll, setShowingAll] = useState(false); // حالة جديدة
    const [searchTerm, setSearchTerm] = useState(''); // حالة منفصلة للبحث
    const containerRef = useRef(null);
    const portalRef = useRef(null);
    const inputRef = useRef(null);
    const [dropdownCoords, setDropdownCoords] = useState(null);
    const [dropdownDirection, setDropdownDirection] = useState('down');
    const isRTL = language === 'ar';

    const defaultFilterFunction = (item, searchTerm) => {
      const label = String(getItemLabel(item) || '').toLowerCase();
      const search = String(searchTerm || '').toLowerCase();
      return label.includes(search);
    };

    useEffect(() => {
      // لو عرض الكل، نعرض كل الـ items
      if (showingAll) {
        // when user requests "show all" show full list (no max truncation)
        setFilteredItems(items || []);
        setHighlightedIndex(-1);
        return;
      }
      // If search is empty, show initial items (first `maxItems`) instead of nothing
      if (!String(searchTerm || '').trim()) {
        setFilteredItems((items || []).slice(0, maxItems));
        setHighlightedIndex(-1);
        return;
      }

      const filterFunc = filterFunction || defaultFilterFunction;
      const filtered = (items || [])
        .filter(item => filterFunc(item, searchTerm))
        .slice(0, maxItems);
      setFilteredItems(filtered);
      setHighlightedIndex(-1);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm, items, maxItems, showingAll, filterFunction, getItemLabel]);

    useEffect(() => {
      const handleClickOutside = (event) => {
        const container = containerRef.current;
        const portal = portalRef.current;
        const clickedInsideContainer = container && container.contains(event.target);
        const clickedInsidePortal = portal && portal.contains(event.target);
        if (!clickedInsideContainer && !clickedInsidePortal) {
          setShowDropdown(false);
          setSearchTerm(''); // Clear search when closing dropdown
          setShowingAll(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // compute dropdown coordinates for portal rendering to escape stacking contexts
    const updateDropdownCoords = () => {
      const inputEl = inputRef.current;
      if (!inputEl) return;
      const rect = inputEl.getBoundingClientRect();
      // estimate dropdown height to decide whether to open up or down
      const headerHeight = 48; // header area showing results count
      const itemHeight = 44; // approx per-item height
      const maxHeight = parseInt(baseDropdownStyle.maxHeight, 10) || 300;
      const estimatedHeight = Math.min(maxHeight, headerHeight + (filteredItems.length * itemHeight));
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openUp = spaceBelow < estimatedHeight && spaceAbove > spaceBelow;

      setDropdownDirection(openUp ? 'up' : 'down');

      setDropdownCoords({
        // for fixed positioning we use viewport coordinates
        top: openUp ? (rect.top - estimatedHeight) : rect.bottom,
        left: rect.left,
        width: rect.width,
        height: estimatedHeight,
      });
    };

    useEffect(() => {
      if (!showDropdown) return;
      updateDropdownCoords();
      const handler = () => updateDropdownCoords();
      window.addEventListener('resize', handler);
      window.addEventListener('scroll', handler, true);
      return () => {
        window.removeEventListener('resize', handler);
        window.removeEventListener('scroll', handler, true);
      };
    }, [showDropdown, showingAll, filteredItems.length]);

    const handleKeyDown = (e) => {
      // Handle Backspace: if user is not typing (no internal searchTerm)
      // and there is a selected value, clear selection instead of doing nothing.
      if (e.key === 'Backspace') {
        if (!searchTerm && value) {
          e.preventDefault();
          handleClear();
          return;
        }
      }

      if (!showDropdown || filteredItems.length === 0) return;
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex(prev => prev < filteredItems.length - 1 ? prev + 1 : 0);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex(prev => prev > 0 ? prev - 1 : filteredItems.length - 1);
          break;
        case 'Enter':
          e.preventDefault();
          if (highlightedIndex >= 0) handleSelect(filteredItems[highlightedIndex]);
          break;
        case 'Escape':
          setShowDropdown(false);
          setShowingAll(false);
          break;
        default:
          break;
      }
    };

    const handleSelect = (item) => {
      // Send the item's id to onChange, not the label
      const itemId = getItemKey(item);
      if (onChange) onChange(itemId);
      if (onSelect) onSelect(item);
      setShowDropdown(false);
      setShowingAll(false);
      setHighlightedIndex(-1);
    };

    const handleInputChange = (e) => {
      const newValue = e.target.value;
      // Update search term for filtering
      setSearchTerm(newValue);
      setShowingAll(false);
      setShowDropdown(true);
    };

    const handleInputFocus = () => {
      // open dropdown on focus so user sees suggestions immediately
      setShowDropdown(true);
    };

    const handleClear = () => {
      // Always clear internal state first
      setSearchTerm('');
      setShowingAll(false);
      setHighlightedIndex(-1);
      
      // If parent provided an explicit onClear handler, use it
      if (typeof onClear === 'function') {
        try { onClear(); } catch (e) { /* ignore */ }
      } else {
        // Default behavior: clear via onChange
        if (onChange) onChange('');
      }

      // Re-open dropdown and focus input
      setShowDropdown(true);
      if (inputRef.current) {
        inputRef.current.focus();
        // ensure portal coords update after focus
        setTimeout(() => {
          try { updateDropdownCoords(); } catch (e) { /* ignore */ }
        }, 0);
      }
    };

    // دالة جديدة لفتح كل الاقتراحات
    const handleArrowClick = () => {
      if (disabled) return;
      setShowingAll(!showingAll);
      // ensure dropdown is open when toggling show-all
      setShowDropdown(true);
      if (inputRef.current) inputRef.current.focus();
    };

    const baseInputStyle = {
      width: '100%',
      padding: '12px 48px 12px 16px', // مساحة للأزرار
      paddingRight: isRTL ? '16px' : '48px',
      paddingLeft: isRTL ? '48px' : '16px',
      borderRadius: '12px',
      border: theme === 'dark' ? '1px solid rgba(148, 163, 184, 0.3)' : '1px solid rgba(148, 163, 184, 0.3)',
      background: theme === 'dark' ? 'rgba(71, 85, 105, 0.4)' : 'rgba(226, 232, 240, 0.6)',
      color: theme === 'dark' ? '#f1f5f9' : '#334155',
      fontSize: '14px',
      fontWeight: '500',
      outline: 'none',
      transition: 'all 0.3s ease',
      textAlign: isRTL ? 'right' : 'left',
      boxSizing: 'border-box',
      ...inputStyle,
    };

    const baseDropdownStyle = {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      background: theme === 'dark' ? 'rgba(15, 23, 42, 0.95)' : '#ffffff',
      border: theme === 'dark' ? '1px solid rgba(148, 163, 184, 0.12)' : '1px solid rgba(148, 163, 184, 0.3)',
      borderRadius: '12px',
      zIndex: 99999,
      marginTop: '4px',
      maxHeight: '300px',
      overflowY: 'auto',
      direction: isRTL ? 'rtl' : 'ltr',
      ...dropdownStyle,
    };

    const baseItemStyle = {
      padding: '12px 16px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      fontSize: '14px',
      color: theme === 'dark' ? '#f1f5f9' : '#334155',
      borderBottom: theme === 'dark' ? '1px solid rgba(148, 163, 184, 0.1)' : '1px solid #f3f4f6',
      transition: 'all 0.2s ease',
      flexDirection: isRTL ? 'row-reverse' : 'row',
      ...itemStyle,
    };

    const defaultRenderItem = (item) => (
      <div style={{ flex: 1, textAlign: isRTL ? 'right' : 'left' }}>
        <div style={{ fontWeight: 600 }}>{getItemLabel(item)}</div>
      </div>
    );

    // Get selected item label to display in input
    const getSelectedLabel = () => {
      // If user is typing, show search term
      if (showDropdown && searchTerm) {
        return searchTerm;
      }
      // Otherwise, show selected value label
      if (!value) return '';
      const selectedItem = items.find(item => {
        const itemId = getItemKey(item);
        const itemLabel = getItemLabel(item);
        // Match by id or by label
        return itemId === value || itemLabel === value;
      });
      return selectedItem ? getItemLabel(selectedItem) : String(value);
    };

    return (
      <div ref={containerRef} data-reusable-dropdown="true" style={{ position: 'relative', width: '100%' }}>
        <div style={{ position: 'relative' }}>
          <input
            ref={inputRef}
            type="text"
            value={getSelectedLabel()}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            autoComplete="off"
            style={baseInputStyle}
          />

          <div style={{
            position: 'absolute',
            left: isRTL ? '8px' : 'auto',
            right: isRTL ? 'auto' : '8px',
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            gap: '4px',
            alignItems: 'center',
          }}>
            {value && !disabled && showClear && (
              <button
                type="button"
                onClick={handleClear}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: theme === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(26,26,26,0.6)',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'color 0.2s ease',
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = theme === 'dark' ? 'rgba(255,255,255,0.9)' : 'rgba(26,26,26,0.9)'}
                onMouseLeave={(e) => e.currentTarget.style.color = theme === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(26,26,26,0.6)'}
              >
                <X size={16} />
              </button>
            )}

            {showAllOnArrowClick && (
              <button
                type="button"
                onClick={handleArrowClick}
                disabled={disabled}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  color: theme === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(26,26,26,0.6)',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                  transform: showDropdown && showingAll ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
                onMouseEnter={(e) => !disabled && (e.currentTarget.style.color = theme === 'dark' ? 'rgba(255,255,255,0.9)' : 'rgba(26,26,26,0.9)')}
                onMouseLeave={(e) => e.currentTarget.style.color = theme === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(26,26,26,0.6)'}
              >
                <ChevronDown size={18} />
              </button>
            )}
          </div>
        </div>

        {showDropdown && filteredItems.length > 0 && (
          // render dropdown in a portal so it escapes parent stacking contexts
          (typeof document !== 'undefined' && dropdownCoords)
            ? createPortal(
              <div
                ref={portalRef}
                style={{
                  position: 'fixed',
                  top: dropdownCoords.top + 'px',
                  left: dropdownCoords.left + 'px',
                  width: dropdownCoords.width + 'px',
                  background: baseDropdownStyle.background,
                  border: baseDropdownStyle.border,
                  borderRadius: baseDropdownStyle.borderRadius,
                  zIndex: baseDropdownStyle.zIndex || 99999,
                  marginTop: dropdownDirection === 'down' ? '4px' : 0,
                  maxHeight: baseDropdownStyle.maxHeight,
                  overflowY: baseDropdownStyle.overflowY,
                  direction: baseDropdownStyle.direction,
                }}
              >
                <div style={{
                  padding: '12px 16px',
                  borderBottom: theme === 'dark' ? '1px solid rgba(148, 163, 184, 0.1)' : '1px solid #f3f4f6',
                  fontSize: '12px',
                  color: theme === 'dark' ? 'rgba(203, 213, 225, 0.6)' : '#6b7280',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  textAlign: isRTL ? 'right' : 'left',
                }}>
                  {filteredItems.length} {language === 'ar' ? 'نتيجة' : 'results'}
                </div>

                {filteredItems.map((item, index) => {
                  const isHighlighted = index === highlightedIndex;
                  const itemKey = getItemKey(item);
                  return (
                    <div
                      key={itemKey}
                      style={{ ...baseItemStyle, background: isHighlighted ? (theme === 'dark' ? 'rgba(59, 130, 246, 0.12)' : 'rgba(59, 130, 246, 0.08)') : 'transparent' }}
                      onClick={() => handleSelect(item)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                    >
                      {renderItem ? renderItem(item, theme, language) : defaultRenderItem(item)}
                    </div>
                  );
                })}
              </div>,
              document.body
            )
            : null
        )}

        {showDropdown && String(value || '').trim() && filteredItems.length === 0 && !showingAll && (
          <div style={baseDropdownStyle}>
            <div style={{ padding: '20px 16px', textAlign: 'center', color: theme === 'dark' ? 'rgba(203, 213, 225, 0.5)' : '#6b7280', fontSize: '14px' }}>{emptyMessage}</div>
          </div>
        )}

        <style>{`
          div[style*="overflowY: auto"]::-webkit-scrollbar { width: 6px; }
          div[style*="overflowY: auto"]::-webkit-scrollbar-track { background: transparent; }
          div[style*="overflowY: auto"]::-webkit-scrollbar-thumb { background: ${theme === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'}; border-radius: 3px; }
          div[style*="overflowY: auto"]::-webkit-scrollbar-thumb:hover { background: ${theme === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)'}; }
        `}</style>
      </div>
    );
  };

  // Named export: unified control style for inputs to match dropdown appearance
  export const unifiedControlStyle = (theme, isRTL = false) => ({
    width: '100%',
    padding: '12px 16px',
    borderRadius: '12px',
    border: theme === 'dark' ? '1px solid rgba(148, 163, 184, 0.2)' : '1px solid rgba(15, 23, 42, 0.1)',
    background: theme === 'dark' ? 'rgba(15, 23, 42, 0.5)' : 'rgba(248, 250, 252, 0.8)',
    color: theme === 'dark' ? '#f1f5f9' : '#1e293b',
    fontSize: '14px',
    fontWeight: '500',
    outline: 'none',
    transition: 'all 0.3s ease',
    textAlign: isRTL ? 'right' : 'left',
    boxSizing: 'border-box',
  });

  export default ReusableDropdown;